import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { Modal } from "@/Common/Components/Modals/Modal";
import { ClinicianSearchStrategy, PersonSearchAutocompleteCustom } from "@/Common/Components/Search/PersonSearchAutocompleteCustom";
import { DocumentTreeClassControl } from "@/Common/Helpers/DocumentTreeClassControl";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { LuxonDateHelper } from "@/Common/Helpers/LuxonDateHelper";
import { MyleStaticResources } from "@/Common/Localization/MyleStaticResouces";
import { DocumentClassModel } from "@/Common/Models/DocumentClassModel";
import { Note } from "@/Common/Models/Note";
import { Person } from "@/Common/Models/Person/Person";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { IDocumentCreateEditUIModel, IDocumentInformation } from "@/Documents/Models/UI/IDocumentCreateEditUIModel";

import styles from "./DocumentNotesPopup.module.scss";

interface IDocumentNotesPopupProps {
    model: IDocumentCreateEditUIModel;
    notes: Note[];
    referenceId: string;
    controlId?: string;
    target?: number;
    subReferenceId?: string;
    eventToDispatchOnDropdownClosed?: number;
    onCancelClick?: () => void;
    onSaveClick?: (documentData: IDocumentInformation, isDocumentApproved: boolean) => void;
}

class DocumentNotesPopupState {
    public note: string;
    public source: string;
    public class: string;
    public subClass: string;
    public description: string;
    public date: Date;
    public selectedClinicians: Immutable.List<Person> = null;
    public didSelectedCliniciansChange: boolean = false;
    public documentClasses: Immutable.List<DocumentClassModel> = Immutable.List<DocumentClassModel>();
    public sourcesAutocomplete: Immutable.List<SearchableItemModelT<string>> = Immutable.List<SearchableItemModelT<string>>();
    public docDescriptionAutocomplete: Immutable.List<SearchableItemModelT<string>> = Immutable.List<SearchableItemModelT<string>>();
    public isApproveDocumentCheckboxChecked: boolean = false;

    constructor(s?: DocumentNotesPopupState) {
        if (s != null) {
            this.class = s.class;
            this.subClass = s.subClass;
            this.description = s.description;
            this.date = s.date;
            this.source = s.source;
            this.note = s.note;
            this.selectedClinicians = s.selectedClinicians;
            this.didSelectedCliniciansChange = s.didSelectedCliniciansChange;
            this.documentClasses = s.documentClasses;
            this.sourcesAutocomplete = s.sourcesAutocomplete;
            this.docDescriptionAutocomplete = s.docDescriptionAutocomplete;
            this.isApproveDocumentCheckboxChecked = s.isApproveDocumentCheckboxChecked;
        }
        else {
            this.class = "";
            this.subClass = "";
            this.description = "";
            this.source = "";
            this.note = "";
            this.date = new Date();
        }
    }
}

class DocumentNotesPopup extends BaseReactComponent<IDocumentNotesPopupProps, DocumentNotesPopupState> {
    private readonly cliniciansSearchStrategy: ClinicianSearchStrategy = new ClinicianSearchStrategy(cacheStore.GetClinicians(false));
    private sending: boolean;

    constructor(props: IDocumentNotesPopupProps) {
        super(props);

        const currentDocument = props.model.get("MedicalDocumentInfo").Model;

        const initialState = new DocumentNotesPopupState();
        initialState.class = currentDocument.ClassId ?? "";
        initialState.subClass = currentDocument.SubClassId ?? "";
        initialState.description = currentDocument.Subject ?? "";
        initialState.source = currentDocument.Source ?? "";
        initialState.date = currentDocument.ReceiptDate;
        initialState.selectedClinicians = props.model.get("SelectedClinicians");
        initialState.documentClasses = DocumentTreeClassControl.GetDocumentClasses().toList();
        initialState.sourcesAutocomplete = !cacheStore.DocumentSources
            ? Immutable.List<SearchableItemModelT<string>>()
            : Immutable.List(cacheStore.DocumentSources.map(p => new SearchableItemModelT<string>(p)));
        initialState.docDescriptionAutocomplete = Immutable.List(new MyleStaticResources().GetDocumentDescription().map(p => new SearchableItemModelT<string>(p)));
        this.state = initialState;

        this.sending = false;
    }

    public componentDidMount(): void {
        if (this.props.controlId) {
            let self = this;
            $("#" + this.props.controlId).on("opened.fndtn.dropdown", function() { $(this).find("textarea").css({ height: "40px" }); });
            $("#" + this.props.controlId).on("closed.fndtn.dropdown", () => {
                var operation = new LocalEvent(self.props.eventToDispatchOnDropdownClosed);
                LocalEventDispatcher.dispatch(operation);
            });
        }
        let elem = document.getElementById("memo_doctb");
        autosize(elem);
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        let elem = document.getElementById("memo_doctb");
        let event = document.createEvent("Event");
        event.initEvent("autosize:destroy", true, false);
        elem.dispatchEvent(event);
    }

    public shouldComponentUpdate(nextProp: IDocumentNotesPopupProps, nextState: DocumentNotesPopupState): boolean {
        return this.props.referenceId !== nextProp.referenceId
            || this.props.subReferenceId !== nextProp.subReferenceId
            || this.props.target !== nextProp.target
            || this.props.model.get("SelectedClinicians") !== nextProp.model.get("SelectedClinicians")
            || this.stateChanged(nextState);
    }

    private renderModalFooter(): JSX.Element {
        let currentDocument = this.props.model.get("MedicalDocumentInfo").Model;
        let isDisabled = this.state.note.trim().length === 0 &&
            this.state.source == currentDocument.Source &&
            this.state.class == currentDocument.ClassId &&
            this.state.subClass == currentDocument.SubClassId &&
            this.state.description == currentDocument.Subject &&
            this.state.date == currentDocument.ReceiptDate &&
            !this.state.didSelectedCliniciansChange &&
            !this.state.isApproveDocumentCheckboxChecked;

        return (
            <div className="text-right">
                {!this.isCurrentDocumentApproved() && (
                    <CheckboxControl
                        checked={this.state.isApproveDocumentCheckboxChecked}
                        onChange={checked => this.setState({ isApproveDocumentCheckboxChecked: checked })}
                        label={cacheStore.GetString("ApproveDocumentLabel")}
                        labelClassName="push-right-10"
                        dataCy="documentNotesPopup-approveDocument"
                    />
                )}
                <button
                    className="btn btn-default push-right-5"
                    onClick={() => this.cancelChanges()}
                >
                    {cacheStore.GetString("CancelLabel")}
                </button>
                <button
                    className="btn btn-primary"
                    data-cy="documentNotesPopup-save"
                    disabled={isDisabled}
                    onClick={() => sessionStore.validatePrescriptionRenewalBeforeDocumentSave(currentDocument.PrescriptionRenewal, this.state.class, () => this.saveDocument())}
                >
                    {cacheStore.GetString("SaveLabel")}
                </button>
            </div>
        );
    }

    public render(): JSX.Element {
        const content = !Array.isArray(this.props.notes) || this.props.notes.length === 0
            ? this.renderEmpty()
            : this.renderWithNotes();

        return (
            <Modal
                title={cacheStore.GetString("DocumentDetailsLabel")}
                onClose={() => this.cancelChanges()}
                footer={this.renderModalFooter()}
                width={700}
                draggable
                disableOverlayClose
                maxHeight="calc(100vh - 150px)"
                top={20}
                overlayZIndex={29}  // should be lower than the z-index of .global-overlay
                showOverlay
                showX
            >
                {content}
            </Modal>
        );
    }

    private renderMemoTextArea(): JSX.Element {
        return (
            <>
                <textarea
                    className={`form-control ${styles.margingMemoTextArea}`}
                    value={this.state.note}
                    id="memo_doctb"
                    onChange={e => this.onTexboxChange(e)}
                    rows={4}
                />
            </>
        );
    }

    private renderWithNotes(): JSX.Element {
        return (
            <>
                {this.renderEditControls()}
                <section className={styles.notes}>
                    <header className={styles.title}>
                        <h4>{cacheStore.GetString("NotesLabel")}</h4>
                    </header>
                    <div>
                        {this.props.notes.slice(0).reverse().map((note, i) => this.renderNoteLine(note, i))}
                    </div>
                    {this.renderMemoTextArea()}
                </section>
            </>
        );
    }

    private renderNoteLine(note: Note, idx: number): JSX.Element {
        let person = cacheStore.GetCliniciansPersonsByContactId(note.ContactId);
        const personNameWithPrefix = `${person.prefix} ${person.FirstName} ${person.LastName}`;

        return (
            <div key={`${note.NoteId}_${idx}`}>
                <div>
                    <label className={styles.editControlLabel}>{personNameWithPrefix}</label>
                    <span>
                        {`${LuxonDateHelper.formatToFullDateTime(note.NoteDate)}`}
                    </span>
                </div>
                <div>
                    {JSXHelper.BreakStringIntoParagraphs(note.NoteContent)}
                </div>
            </div>
        );
    }

    private renderEmpty(): JSX.Element {
        return (
            <div>
                {this.renderEditControls()}
                {this.renderMemoTextArea()}
            </div>
        );
    }

    private renderEditControls(): JSX.Element {
        const uploadDate = this.props.model?.get("MedicalDocumentInfo")?.Model?.UploadDate;

        return (
            <div>
                <div>
                    <label className={styles.editControlLabel}>{cacheStore.GetString("ClassLabel")}</label>
                    <select
                        className="form-control myle-control"
                        onChange={e => this.onClassSubClassSelectionChange(e)}
                        value={`${this.state.class}&${this.state.subClass}`}
                        data-cy="documentNotesPopup-class"
                    >
                        {this.state.documentClasses.toArray().map(documentClass => (
                            <optgroup key={documentClass.LookupId} label={documentClass.Name}>
                                {documentClass.SubClasses.toArray().map(subClass => {
                                    if (this.state.subClass === subClass.LookupId) {
                                        return (
                                            <option
                                                key={subClass.LookupId}
                                                value={`${documentClass.LookupId}&${subClass.LookupId}`}
                                            >{`${cacheStore.GetLocalizedValueByLookupId(documentClass.LookupId)} - ${cacheStore.GetLocalizedValueByLookupId(subClass.LookupId)}`}
                                            </option>
                                        );
                                    }
                                    else {
                                        return (
                                            <option
                                                key={subClass.LookupId}
                                                value={`${documentClass.LookupId}&${subClass.LookupId}`}
                                            >{`${cacheStore.GetLocalizedValueByLookupId(documentClass.LookupId)} - ${cacheStore.GetLocalizedValueByLookupId(subClass.LookupId)}`}
                                            </option>
                                        );
                                    }
                                })}
                            </optgroup>
                        ))}
                    </select>
                </div>
                <div className={styles.formRow}>
                    <label className={styles.editControlLabel}>{cacheStore.GetString("SourceLabel")}</label>
                    <TextboxControl
                        autoCompleteItems={this.state.sourcesAutocomplete}
                        value={this.state.source}
                        onChange={value => this.documentSourceChanged(value)}
                        onAutoCompleteSelectCallBack={(code, model, value) => this.documentSourceSelected(value)}
                    />
                </div>
                <div className={styles.formRow}>
                    <label className={styles.editControlLabel}>{cacheStore.GetString("DescriptionDLabel")}</label>
                    <TextboxControl
                        autoCompleteItems={this.state.docDescriptionAutocomplete}
                        value={this.state.description}
                        onChange={value => this.documentDescriptionChanged(value)}
                        onAutoCompleteSelectCallBack={(code, model, value) => this.documentDescriptionSelected(value)}
                    />
                </div>
                <div className={styles.formRow}>
                    <label className={styles.editControlLabel}>{cacheStore.GetString("ResponsibleProvidersLabel")}</label>
                    <PersonSearchAutocompleteCustom
                        componentId="edit_doc_clinicians"
                        selectedItemList={this.state.selectedClinicians}
                        focusOnLoad={false}
                        closeDropDownAfterSelection={true}
                        searchStrategy={this.cliniciansSearchStrategy}
                        allowMultipleItemSelection={true}
                        onSelectionCallback={provider => this.selectResposibleProvider(provider.Item)}
                        onClearCallback={provider => this.clearResponsibleProvider(provider.Item)}
                    />
                </div>
                <div className={styles.formRow}>
                    <label className={styles.editControlLabel}>{cacheStore.GetString("DocumentDateLabel")}</label>
                    <DateSelector
                        id="doc_create_date_p"
                        date={this.state.date}
                        dateFormat="yyyy-MM-dd"
                        onChangeCallback={date => this.documentDateChanged(date)}
                    />
                </div>
                {uploadDate && (
                    <div className={styles.formRow}>
                        <label className={styles.editControlLabel}>{cacheStore.GetString("DocumentNotesPopup_UploadedOnLabel")}:</label>
                        <span>{LuxonDateTimeParser.parse(uploadDate)?.toFormat("yyyy-MM-dd")}</span>
                    </div>
                )}
            </div>
        );
    }

    private saveDocument(): void {
        const currentDocument = this.props.model.get("MedicalDocumentInfo").Model;
        const documentData: IDocumentInformation = {
            referenceId: this.props.referenceId,
            target: this.props.target,
            subReferenceId: this.props.subReferenceId,
            note: this.state.note,
            source: this.state.source,
            docClass: this.state.class,
            docSubClass: this.state.subClass,
            description: this.state.description,
            date: this.state.date,
            documentId: currentDocument.Id,
            isApproved: !!currentDocument.SignOffDate,
            responsibleProvidersIds: this.state.selectedClinicians.toArray().map(p => p.ContactId),
            prescriptionRenewalId: currentDocument?.PrescriptionRenewal?.Id
        };

        if (this.state.isApproveDocumentCheckboxChecked && currentDocument.Id) {
            sessionStore.dispatch(LocalOperations.Documents.Approve, {
                "DocumentIDs": [currentDocument.Id]
            });
        }

        if (this.props.onSaveClick) {
            this.props.onSaveClick(documentData, this.state.isApproveDocumentCheckboxChecked);
            return;
        }

        if (this.sending === false) {
            this.sending = true;

            sessionStore.dispatch(LocalOperations.Documents.Note_Dropdown_Save, { documentData });
        }
    }

    private onTexboxChange(e: React.ChangeEvent<HTMLTextAreaElement>): void {
        var s = new DocumentNotesPopupState(this.state);
        s.note = e.target.value;
        this.setState(s);
    }

    private stateChanged(nextState: DocumentNotesPopupState): boolean {
        return (
            this.state.class !== nextState.class
            || this.state.date !== nextState.date
            || this.state.description !== nextState.description
            || this.state.note !== nextState.note
            || this.state.source !== nextState.source
            || this.state.subClass !== nextState.subClass
            || this.state.selectedClinicians !== nextState.selectedClinicians
            || this.state.documentClasses !== nextState.documentClasses
            || this.state.sourcesAutocomplete !== nextState.sourcesAutocomplete
            || this.state.docDescriptionAutocomplete !== nextState.docDescriptionAutocomplete
            || this.state.isApproveDocumentCheckboxChecked !== nextState.isApproveDocumentCheckboxChecked
        );
    }

    private onClassSubClassSelectionChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        var selected: string = e.target.value;
        var classes = selected.split("&");
        this.setState({
            class: classes[0],
            subClass: classes[1]
        });
    }

    private cancelChanges(): void {
        if (this.props.onCancelClick) {
            this.props.onCancelClick();
            return;
        }

        sessionStore.dispatch(LocalOperations.Documents.CancelNotesDropdown, null);
    }

    private documentSourceSelected(source: string): void {
        this.documentSourceChanged(source);
    }

    private documentSourceChanged(source: string): void {
        var s = new DocumentNotesPopupState(this.state);
        s.source = source;
        this.setState(s);
    }

    private documentDescriptionSelected(description: string): void {
        this.documentDescriptionChanged(description);
    }

    private documentDescriptionChanged(description: string): void {
        var s = new DocumentNotesPopupState(this.state);
        s.description = description;
        this.setState(s);
    }

    private documentDateChanged(date: Date): void {
        var s = new DocumentNotesPopupState(this.state);
        s.date = date;
        this.setState(s);
    }

    private selectResposibleProvider(selectedProvider: Person): void {
        if (!selectedProvider)
            return;

        let responsibleProviders = this.state.selectedClinicians.toArray();

        responsibleProviders.map(x => x.ContactId).indexOf(selectedProvider.ContactId) > -1
            ? responsibleProviders = responsibleProviders.filter(x => x.ContactId !== selectedProvider.ContactId)
            : responsibleProviders.push(selectedProvider);

        this.mutateState(state => {
            state.didSelectedCliniciansChange = true;
            state.selectedClinicians = Immutable.List<Person>(responsibleProviders);
        });
    }

    private clearResponsibleProvider(selectedProvider: Person): void {
        if (!selectedProvider)
            return;

        const filteredClinicians = this.state.selectedClinicians.filterImmutable(x => x.ContactId !== selectedProvider.ContactId);

        this.mutateState(state => {
            state.didSelectedCliniciansChange = true;
            state.selectedClinicians = filteredClinicians;
        });
    }

    private isCurrentDocumentApproved(): boolean {
        const currentDocument = this.props.model?.get("MedicalDocumentInfo")?.Model;

        if (!currentDocument) {
            return false;
        }

        return !!currentDocument.SignOffDate && !!currentDocument.SignOffByContactId;
    }
}

export { DocumentNotesPopup };
